import { PdBaseUI } from "../base/pd-base-ui.js";
import __decorate from "../_virtual/_@oxc-project_runtime@0.147.0/helpers/esm/decorate.js";
import { css, html, nothing } from "lit";
import { property, state } from "lit/decorators.js";
import { msg } from "@lit/localize";
//#region src/pd-form-container/PdFormContainer.ts
/**
* @license
* Copyright (c) 2021 PD Progressive Development UG. All rights reserved.
*/
/**
* Form container for managing and validating pd-* form elements.
*
* @tagname pd-form-container
* @summary Container for form fields with validation and state management.
*
* @event pd-form-change - Fired when any form element registers, unregisters, or changes with `{overallValidity, dirty}` detail. Bubbles and is composed, so ancestors (e.g. a wizard) can observe form state.
*
* @slot - pd-* form elements to manage.
*
* @cssprop --pd-form-container-row-gap - Gap between form rows. Default: `var(--pd-spacing-sm, 0.5rem)`.
* @cssprop --pd-form-info-font-size - Validation info font size. Default: `0.8em`.
* @cssprop --pd-form-container-default-col - Default info text color. Default: `var(--pd-default-dark-col)`.
* @cssprop --pd-form-container-valid-col - Valid state text color. Default: `var(--pd-default-success-dark-col)`.
* @cssprop --pd-form-container-invalid-col - Invalid state text color. Default: `var(--pd-default-error-dark-col)`.
*/
var PdFormContainer = class extends PdBaseUI {
	constructor(..._args) {
		super(..._args);
		this.requiredFieldInfo = false;
		this.autoTrimm = false;
		this.commonError = "";
		this._fields = /* @__PURE__ */ new Map();
		this._onRegister = (e) => {
			const { name, value, parsedValue, state } = e.detail;
			console.debug("###### _onRegister", name, value, state);
			if (!this._fields.has(name)) {
				this._fields.set(name, {
					name,
					value,
					parsedValue,
					valid: state.valid,
					invalid: state.invalid,
					pristine: state.pristine,
					dirty: state.dirty,
					touched: state.touched,
					focused: state.focus,
					errors: {},
					element: e.composedPath()[0] ?? e.target
				});
				this.requestUpdate();
				this._dispatchFormChange();
			}
			e.stopPropagation();
		};
		this._onUnRegister = (e) => {
			const { name, value } = e.detail;
			console.debug("###### _onUnRegister", name, value);
			if (this._fields.has(name)) {
				this._fields.delete(name);
				this.requestUpdate();
				this._dispatchFormChange();
			}
			e.stopPropagation();
		};
		this._onUnRegisterBound = this._onUnRegister.bind(this);
		this._onChange = (e) => {
			const { name, value, parsedValue, state } = e.detail;
			console.debug("###### _onChange", name, value, state);
			const field = this._fields.get(name);
			if (field) {
				const oldValue = field.value;
				field.value = value;
				field.parsedValue = parsedValue;
				field.pristine = state.pristine;
				field.dirty = state.dirty;
				field.touched = state.touched;
				field.focused = state.focused;
				field.valid = state.valid;
				field.invalid = state.invalid;
				this.requestUpdate();
				if (value !== oldValue) this._dispatchFormChange();
			}
			e.stopPropagation();
		};
		this._onBlur = (e) => {
			const { name } = e.detail;
			const field = this._fields.get(name);
			if (field) {
				field.touched = true;
				field.focused = false;
			}
			e.stopPropagation();
		};
		this._onFocus = (e) => {
			const { name } = e.detail;
			const field = this._fields.get(name);
			if (field) field.focused = true;
			e.stopPropagation();
		};
	}
	static {
		this.styles = [PdBaseUI.styles, css`
      :host {
        --_row-gap: var(--pd-form-container-row-gap, var(--pd-spacing-md));

        display: block;
        width: 100%;
      }

      form {
        display: flex;
        flex-direction: column;
        gap: var(--_row-gap);
        width: 100%;
      }

      /* Ensure pd-form-row children take full width */
      ::slotted(pd-form-row) {
        width: 100%;
      }

      .validation-info {
        font-family: var(--pd-default-font-content-family);
        font-size: var(--pd-form-info-font-size, 0.8em);
        padding-top: 0.8em;
        color: var(--pd-form-container-default-col, var(--pd-default-dark-col));
      }

      .valid {
        color: var(
          --pd-form-container-valid-col,
          var(--pd-default-success-dark-col)
        );
      }

      .invalid {
        color: var(
          --pd-form-container-invalid-col,
          var(--pd-default-error-dark-col)
        );
      }

      .error-message {
        color: var(--pd-error-col, #dc3545);
        background-color: var(--pd-error-bg-col, #f8d7da);
        border: 1px solid var(--pd-error-border-col, #f5c6cb);
        border-radius: var(--pd-radius-md);
        padding: 0.75rem 1rem;
        margin-bottom: 1rem;
        font-size: 0.875rem;
        width: fit-content;
      }
    `];
	}
	connectedCallback() {
		super.connectedCallback();
		this.addEventListener("pd-form-element-register", this._onRegister);
		this.addEventListener("pd-form-element-change", this._onChange);
		this.addEventListener("pd-form-element-blur", this._onBlur);
		this.addEventListener("pd-form-element-focus", this._onFocus);
		document.addEventListener("pd-form-element-unregister", this._onUnRegisterBound);
	}
	disconnectedCallback() {
		super.disconnectedCallback();
		this.removeEventListener("pd-form-element-register", this._onRegister);
		this.removeEventListener("pd-form-element-change", this._onChange);
		this.removeEventListener("pd-form-element-blur", this._onBlur);
		this.removeEventListener("pd-form-element-focus", this._onFocus);
		document.removeEventListener("pd-form-element-unregister", this._onUnRegisterBound);
	}
	render() {
		return html`
      <form>
        <slot></slot>
        ${this.requiredFieldInfo || this.commonError ? this._renderValidationInfo() : nothing}
      </form>
    `;
	}
	_renderValidationInfo() {
		const allValid = this._getOverallValidity() && !this.commonError;
		return html`
      ${this.commonError ? html`
              <div class="error-message" role="alert">${this.commonError}</div>
            ` : html`
              <p class="validation-info ${allValid ? "valid" : "invalid"}">
                ${allValid ? msg("* Felder ausgefüllt", { id: "pd.form.required.valid" }) : msg("* Pflichtfeld fehlt oder fehlerhaft", { id: "pd.form.required.missing" })}
              </p>
            `}
    `;
	}
	_getOverallValidity() {
		if (this._fields.size === 0) return true;
		for (const field of this._fields.values()) if (field.invalid) return false;
		return true;
	}
	/** Runs all field validators and returns overall validity. */
	async triggerValidate() {
		const validations = [];
		this._fields.forEach((field) => {
			const result = field.element?.runValidators();
			if (result instanceof Promise) validations.push(result);
		});
		await Promise.all(validations);
		return this._getOverallValidity();
	}
	/** Returns all field values as origin and parsed objects. */
	getValues() {
		const origin = {};
		const parsed = {};
		this._fields.forEach((field, key) => {
			let value = field.value;
			if (this.autoTrimm === true && typeof value === "string") value = value.trim();
			origin[key] = value;
			parsed[key] = field.parsedValue;
		});
		return {
			origin,
			parsed
		};
	}
	/** Resets all form fields to their initial values. */
	reset() {
		this._fields.forEach((field) => {
			const element = field.element;
			if (typeof element?.reset === "function") element.reset();
		});
	}
	/** Clear all form fields. */
	clear() {
		this._fields.forEach((field) => {
			const element = field.element;
			if (typeof element?.clear === "function") element.clear();
		});
	}
	/** Returns the current status map of all registered fields. */
	getStatus() {
		return this._fields;
	}
	/** Whether all form fields are currently valid. */
	get valid() {
		return this._getOverallValidity();
	}
	/**
	* Whether at least one field was changed by the user.
	* Programmatic updates (initValue prefill, `value` setter) do not count —
	* dirty tracks user interaction, anchored per field at its `initValue`.
	*/
	get dirty() {
		for (const field of this._fields.values()) if (field.dirty) return true;
		return false;
	}
	/**
	* Notifies listeners about a form state change. Bubbles and is composed
	* so ancestors outside the shadow root (e.g. a wizard) can observe it.
	*/
	_dispatchFormChange() {
		this.dispatchEvent(new CustomEvent("pd-form-change", {
			detail: {
				overallValidity: this._getOverallValidity(),
				dirty: this.dirty
			},
			bubbles: true,
			composed: true
		}));
	}
};
__decorate([property({ type: Boolean })], PdFormContainer.prototype, "requiredFieldInfo", void 0);
__decorate([property({ type: Boolean })], PdFormContainer.prototype, "autoTrimm", void 0);
__decorate([property({ type: String })], PdFormContainer.prototype, "commonError", void 0);
__decorate([state()], PdFormContainer.prototype, "_fields", void 0);
//#endregion
export { PdFormContainer };
